// src/utils/speech.js
//
// ===========================================================================
// ONE SPEECH ENGINE FOR EVERY GAME
// ===========================================================================
// Read-aloud is currently reimplemented in 31 quiz files. They are near
// copies of each other, and they share the same four faults. Fixing them
// one file at a time means fixing the same bug 31 times and missing some.
//
// More importantly, they are all reaching for the SAME OBJECT.
// window.speechSynthesis is a single browser-wide singleton. It is not
// per-component and not per-React-tree. That is why a bug in one game
// silences all the others, which is exactly what happened when a keepalive
// in DragDropQuiz left the synth paused and MultipleChoice — a file nobody
// had touched — went quiet with it.
//
// This module owns that singleton. Games call speak() and stop() and never
// touch window.speechSynthesis themselves.
//
// ---------------------------------------------------------------------------
// THE FOUR FAULTS THIS FIXES
// ---------------------------------------------------------------------------
//
// 1. cancel() AND speak() IN THE SAME TICK.
//    Present in 31 files. Chrome queues both against the same internal state
//    and silently discards the speak() — no error, no onerror, no onend, no
//    console warning. The utterance simply never happens.
//
//    This is the bug with the signature you kept seeing: the music ducks
//    (that happens immediately, before speaking) and then nothing is said and
//    nothing ever restores the volume, because the only code that restored it
//    lived on the onend of an utterance that never ran.
//
// 2. THE UTTERANCE NOT HELD.
//    A SpeechSynthesisUtterance created as a local const with nothing
//    referencing it can be garbage-collected mid-sentence. Chrome truncates
//    or drops it. Survivable for "Group A", fatal for a full question.
//
// 3. NOTHING GUARANTEES THE MUSIC COMES BACK.
//    Every restore path in the existing files hangs off onend or onerror.
//    Speech can fail without firing either — no voices, a muted tab, a
//    locked-down device. A watchdog here always restores, so the worst case
//    is a silent reading, never a permanently silent game.
//
// 4. FIGHTING OVER onvoiceschanged.
//    Several files do `window.speechSynthesis.onvoiceschanged = handler` and
//    then null it on unmount. It is one property: the last writer wins, and
//    an unmount nulls it for every other component too. This module attaches
//    once with addEventListener and hands voices out to everyone.
//
// ---------------------------------------------------------------------------
// WHAT IT DELIBERATELY DOES NOT DO
// ---------------------------------------------------------------------------
// It never calls speechSynthesis.pause(). The usual trick for Chrome's ~15
// second cutoff is pause() then resume() on a timer. If the queue drains
// between the two, Chrome is left paused with an empty queue and resume()
// does not reliably clear it — at which point every speak() in the whole app
// queues forever. That is a global failure caused by a local optimisation.
// Long text is split into sentence-sized utterances instead, which sidesteps
// the cutoff without ever touching the pause flag.
// ===========================================================================

const HAS_SPEECH =
  typeof window !== "undefined" && "speechSynthesis" in window;

// Roughly 15 seconds of speech at the rates used here. Chrome cuts a single
// utterance off around that point.
const MAX_CHARS = 180;

// How long to wait for onstart before assuming speech failed silently.
const WATCHDOG_MS = 2000;

// The gap between cancel() and speak(). Anything above one macrotask works;
// 80ms is comfortably clear and imperceptible before a spoken sentence.
const CANCEL_GAP_MS = 80;

/* ------------------------------------------------------------------ */
/* Voices                                                              */
/* ------------------------------------------------------------------ */

let voiceCache = [];
let voicesBound = false;

const readVoices = () => {
  if (!HAS_SPEECH) return [];
  voiceCache = window.speechSynthesis.getVoices() || [];
  return voiceCache;
};

const bindVoices = () => {
  if (!HAS_SPEECH || voicesBound) return;
  voicesBound = true;
  readVoices();
  // addEventListener, not onvoiceschanged — see fault 4.
  window.speechSynthesis.addEventListener("voiceschanged", readVoices);
};

if (HAS_SPEECH) bindVoices();

/** Voices are populated asynchronously on first use in Chrome. */
const whenVoicesReady = (callback) => {
  if (!HAS_SPEECH) return callback([]);

  const existing = readVoices();
  if (existing.length) return callback(existing);

  let settled = false;
  const done = () => {
    if (settled) return;
    settled = true;
    window.speechSynthesis.removeEventListener("voiceschanged", done);
    callback(readVoices());
  };

  window.speechSynthesis.addEventListener("voiceschanged", done);
  // Never block on this. A device with no voices must still fall through and
  // let the watchdog restore the music, rather than hanging on a callback.
  setTimeout(done, 1200);
};

export const getVoices = () => readVoices();

export const chooseBestVoice = (list = voiceCache, preferredName = "") => {
  if (!list.length) return null;

  if (preferredName) {
    const named = list.find((v) => v.name === preferredName);
    if (named) return named;
  }

  const natural = list.find(
    (v) => /^en-US/i.test(v.lang) && /natural|neural|google|samantha/i.test(v.name)
  );
  if (natural) return natural;

  return (
    list.find((v) => /^en-US/i.test(v.lang)) ||
    list.find((v) => /^en/i.test(v.lang)) ||
    list[0]
  );
};

/* ------------------------------------------------------------------ */
/* Chunking                                                            */
/* ------------------------------------------------------------------ */

/** Split long text on sentence boundaries so no utterance hits the cutoff. */
export const chunkText = (text, limit = MAX_CHARS) => {
  const clean = String(text || "").trim();
  if (!clean) return [];
  if (clean.length <= limit) return [clean];

  const sentences = clean.match(/[^.!?]+[.!?]*\s*/g) || [clean];
  const chunks = [];
  let buffer = "";

  sentences.forEach((sentence) => {
    if ((buffer + sentence).length > limit && buffer) {
      chunks.push(buffer.trim());
      buffer = sentence;
    } else {
      buffer += sentence;
    }
  });

  if (buffer.trim()) chunks.push(buffer.trim());
  return chunks;
};

/* ------------------------------------------------------------------ */
/* The engine                                                          */
/* ------------------------------------------------------------------ */

// Incremented on every stop() and every new speak(). Any callback carrying a
// stale token is from a run that has been superseded and is ignored. This is
// what stops a cancelled utterance's onend from advancing the new queue.
let token = 0;

// Fault 2: the live utterance is held here for its whole life.
let liveUtterance = null;
let watchdog = null;

const clearWatchdog = () => {
  if (watchdog) {
    clearTimeout(watchdog);
    watchdog = null;
  }
};

/** Stop everything immediately. Safe to call at any time, including unmount. */
export const stop = () => {
  token += 1;
  liveUtterance = null;
  clearWatchdog();
  if (HAS_SPEECH) window.speechSynthesis.cancel();
};

/**
 * Speak one or more steps in order.
 *
 * @param {Array|string} input  A string, or [{ text, key, rate }, ...].
 *   `key` is passed back to onStep so the caller can highlight whatever is
 *   being read — a drop zone, an answer card — without this module knowing
 *   anything about the UI.
 * @param {object} handlers
 *   onStep(key)  fired as each step begins; called with null when finished
 *   onDone()     fired once, whether it succeeded, errored, or timed out
 *
 * @returns {Function} a cancel function for this specific run.
 */
export const speak = (input, handlers = {}) => {
  const { onStep, onDone } = handlers;

  const finishOnce = (() => {
    let called = false;
    return () => {
      if (called) return;
      called = true;
      onStep?.(null);
      onDone?.();
    };
  })();

  if (!HAS_SPEECH) {
    // No API at all. Call onDone so the caller restores its music instead of
    // waiting forever for a callback that cannot come.
    finishOnce();
    return () => {};
  }

  const steps = (Array.isArray(input) ? input : [{ text: input }])
    .filter((step) => step && step.text)
    .flatMap((step) =>
      chunkText(step.text).map((text, i) => ({
        ...step,
        text,
        // Only the first fragment of a long line carries the highlight, so a
        // chunked sentence does not re-trigger the pulse on every piece.
        key: i === 0 ? step.key ?? null : null,
      }))
    );

  if (!steps.length) {
    finishOnce();
    return () => {};
  }

  stop();
  const myToken = token;
  const isStale = () => myToken !== token;

  const runStep = (index, voices) => {
    if (isStale()) return;

    if (index >= steps.length) {
      clearWatchdog();
      finishOnce();
      return;
    }

    const step = steps[index];
    onStep?.(step.key ?? null);

    const utterance = new SpeechSynthesisUtterance(step.text);
    utterance.lang = step.lang || "en-US";
    utterance.rate = step.rate ?? 0.95;
    utterance.pitch = step.pitch ?? 1;
    utterance.volume = 1;

    const voice = chooseBestVoice(voices, step.voiceName);
    if (voice) utterance.voice = voice;

    const next = () => {
      if (isStale()) return;
      clearWatchdog();
      runStep(index + 1, voices);
    };

    utterance.onstart = () => {
      if (isStale()) return;
      clearWatchdog();
    };
    utterance.onend = next;
    utterance.onerror = next;

    liveUtterance = utterance;   // fault 2

    // resume() unconditionally. It is a no-op on a running synth, and it is
    // the only thing that recovers a tab left paused by an older build.
    window.speechSynthesis.resume();
    window.speechSynthesis.speak(utterance);

    // Fault 3. If onstart has not fired and nothing is speaking, speech has
    // failed in a way that raises no event. Give up on the whole run rather
    // than leaving the caller's music ducked forever.
    clearWatchdog();
    watchdog = setTimeout(() => {
      if (isStale()) return;
      if (!window.speechSynthesis.speaking) finishOnce();
    }, WATCHDOG_MS);
  };

  whenVoicesReady((voices) => {
    if (isStale()) return;
    // Fault 1. cancel() has already run inside stop(); this gap is what lets
    // it land before speak(). Without it Chrome discards the utterance.
    setTimeout(() => {
      if (isStale()) return;
      runStep(0, voices);
    }, CANCEL_GAP_MS);
  });

  return () => {
    if (myToken === token) stop();
  };
};

export const isSupported = () => HAS_SPEECH;

/**
 * Diagnostic, callable from the browser console as:
 *   import("./utils/speech").then(s => s.diagnose().then(console.log))
 *
 * Reports what the browser will actually do, rather than what it claims.
 */
export const diagnose = () =>
  new Promise((resolve) => {
    if (!HAS_SPEECH) {
      return resolve({ supported: false, reason: "speechSynthesis missing" });
    }

    whenVoicesReady((voices) => {
      const report = {
        supported: true,
        pausedOnEntry: window.speechSynthesis.paused,
        voiceCount: voices.length,
        englishVoices: voices.filter((v) => /^en/i.test(v.lang)).length,
        sameTickSpoke: null,
        delayedSpoke: null,
      };

      // Same tick — the broken pattern.
      window.speechSynthesis.cancel();
      const a = new SpeechSynthesisUtterance("Test one.");
      a.onstart = () => { report.sameTickSpoke = true; };
      window.speechSynthesis.speak(a);

      setTimeout(() => {
        if (report.sameTickSpoke === null) report.sameTickSpoke = false;

        // Separated by a tick — the fix.
        window.speechSynthesis.cancel();
        setTimeout(() => {
          const b = new SpeechSynthesisUtterance("Test two.");
          b.onstart = () => { report.delayedSpoke = true; };
          liveUtterance = b;
          window.speechSynthesis.resume();
          window.speechSynthesis.speak(b);

          setTimeout(() => {
            if (report.delayedSpoke === null) report.delayedSpoke = false;
            window.speechSynthesis.cancel();
            resolve(report);
          }, 1400);
        }, CANCEL_GAP_MS);
      }, 1400);
    });
  });

export default { speak, stop, isSupported, getVoices, chooseBestVoice, chunkText, diagnose };
